import React, { useState, useRef, useCallback, useEffect } from 'react';
import { X, Printer, Download, Loader2, AlertCircle, FileText } from 'lucide-react';
import comprobanteISLRTemplateHtml from './ComprobanteISLRTemplate';

const MAX_RETRIES = 50;

const CONCEPTOS_MAP = {
  honorarios: 'Honorarios Profesionales',
  comisiones: 'Comisiones Mercantiles',
  transporte: 'Transporte Terrestre',
  servicios: 'Servicios (incluye suministro)',
  publicidad: 'Publicidad y Propaganda',
  arrendamiento_muebles: 'Arrendamiento de bienes muebles',
  arrendamiento_inmuebles: 'Arrendamiento de bienes inmuebles'
};

const transformData = (factura) => {
  if (!factura) return null;

  const now = new Date();
  const fechaEmision = now.toISOString().split('T')[0];

  const facturaFecha = factura.fecha_creacion
    ? new Date(factura.fecha_creacion).toISOString().split('T')[0]
    : fechaEmision;

  const nroComprobante = `ISLR-${factura.numero_cotizacion || ('COT' + (factura.id || ''))}-${String(now.getTime()).slice(-4)}`;

  return {
    numero: nroComprobante,
    fechaEmision: fechaEmision,
    agente: {
      nombre: 'ELECTROSOLUCIONES GAMAN C.A',
      rif: 'J-41129322-9',
      direccion: 'CALLE ROSCIO EDIF RESIDENCIAS MARIANA PISO PB LOCAL 3 SECTOR SAN JUAN DE LOS MORROS GUARICO ZONA POSTAL 2301',
      telefono: '0412-0803358',
      ciudad: 'SAN JUAN DE LOS MORROS',
      estado: 'GUÁRICO'
    },
    sujeto: {
      nombre: factura.cliente_nombre || factura.empresa_nombre || '',
      rif: factura.empresa_rif || '',
      telefono: '',
      ciudad: '',
      estado: ''
    },
    factura: {
      fechaEmision: facturaFecha,
      numero: factura.numero_cotizacion || `COT-${factura.id}`,
      numeroControl: '',
      baseImponible: factura.baseImponible || 0,
      iva: factura.iva || 0
    },
    islr: {
      codigo: factura.islr_concepto_codigo || '',
      concepto: CONCEPTOS_MAP[factura.islr_concepto_codigo] || '',
      montoObjeto: factura.baseImponible || 0,
      porcentaje: factura.islr_porcentaje || 0,
      retenido: factura.islr || 0
    }
  };
};

const ComprobanteISLRPrintModal = ({ onClose, data }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const iframeRef = useRef(null);
  const datosInyectados = useRef(false);
  const retryCount = useRef(0);
  const htmlContent = comprobanteISLRTemplateHtml;

  const datosComprobante = data ? transformData(data) : null;

  const inyectarDatos = useCallback(() => {
    if (!iframeRef.current || datosInyectados.current || !datosComprobante) return;
    try {
      const win = iframeRef.current.contentWindow;
      if (win && typeof win.renderComprobanteISLR === 'function') {
        win.renderComprobanteISLR(datosComprobante);
        datosInyectados.current = true;
        setLoading(false);
      } else if (retryCount.current < MAX_RETRIES) {
        retryCount.current += 1;
        setTimeout(inyectarDatos, 100);
      } else {
        setError('No se pudo cargar el comprobante (tiempo de espera agotado)');
        setLoading(false);
      }
    } catch (err) {
      console.error('Error al inyectar datos:', err);
      setError('Error al renderizar el comprobante');
      setLoading(false);
    }
  }, [datosComprobante]);

  useEffect(() => {
    if (!datosComprobante) {
      setError('No hay datos de ISLR para generar el comprobante');
      setLoading(false);
    }
  }, [datosComprobante]);

  const handleIframeLoad = useCallback(() => {
    inyectarDatos();
  }, [inyectarDatos]);

  const handlePrint = () => {
    if (iframeRef.current) {
      iframeRef.current.contentWindow.print();
    }
  };

  const handleDownload = () => {
    if (iframeRef.current) {
      if (typeof iframeRef.current.contentWindow.descargarPDF === 'function') {
        iframeRef.current.contentWindow.descargarPDF();
      } else {
        iframeRef.current.contentWindow.print();
      }
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl h-[90vh] flex flex-col overflow-hidden">
        <div className="bg-gray-100 border-b p-4 flex justify-between items-center">
          <div>
            <h2 className="text-xl font-bold flex items-center gap-2">
              <FileText className="text-gray-600" />
              Comprobante Retención ISLR
            </h2>
            <p className="text-sm text-gray-500">Vista previa del comprobante</p>
          </div>
          <div className="flex gap-2">
            {!loading && !error && (
              <>
                <button
                  onClick={handleDownload}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium flex items-center gap-2"
                >
                  <Download size={18} /> Descargar PDF
                </button>
                <button
                  onClick={handlePrint}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium flex items-center gap-2"
                >
                  <Printer size={18} /> Imprimir
                </button>
              </>
            )}
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 font-medium flex items-center gap-2"
            >
              <X size={18} /> Cerrar
            </button>
          </div>
        </div>

        <div className="flex-1 bg-gray-200 relative overflow-hidden flex justify-center items-center">
          {loading && (
            <div className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-white/80 backdrop-blur-sm">
              <Loader2 size={48} className="animate-spin text-blue-600 mb-4" />
              <p className="text-lg font-medium text-gray-700">Generando documento...</p>
            </div>
          )}

          {error && (
            <div className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-white">
              <AlertCircle size={64} className="text-red-500 mb-4" />
              <p className="text-xl font-bold text-gray-800 mb-2">Error al cargar datos</p>
              <p className="text-gray-600 max-w-md text-center">{error}</p>
            </div>
          )}

          <iframe
            ref={iframeRef}
            srcDoc={htmlContent}
            onLoad={handleIframeLoad}
            className={`w-full h-full bg-white transition-opacity duration-300 ${loading || error ? 'opacity-0' : 'opacity-100'}`}
            title="Comprobante Retención ISLR"
          />
        </div>
      </div>
    </div>
  );
};

export default ComprobanteISLRPrintModal;
